import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Card, EmptyState, PageHeader } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Plus, Clock, UserCheck, AlertCircle, Sparkles, RefreshCw, RotateCcw } from "lucide-react";
import { DuplicateGuard } from "@/components/DuplicateGuard";
import { AppointmentsPanel } from "@/components/AppointmentsPanel";
import { LeadMergeDialog } from "@/components/LeadMergeDialog";
import { WhatsAppButton, WinBackButton, WinBackDialog } from "@/components/WhatsAppButton";
import { StaffFilterSelect, useStaffParam } from "@/components/StaffFilterSelect";
import { LeadNoteLog } from "@/components/ClientNoteLog";

const todayStr = () => new Date().toISOString().slice(0, 10);
const SCORE_STYLE = { Hot: "bg-rose-100 text-rose-700", Warm: "bg-amber-100 text-amber-700", Cold: "bg-sky-100 text-sky-700" };
const ScoreBadge = ({ score, testid }) => score ? (
  <span data-testid={testid} title={score.reason} className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${SCORE_STYLE[score.label] || "bg-slate-100 text-slate-600"}`}><Sparkles className="h-3 w-3" />{score.label}</span>
) : null;
const RATINGS = ["Hot", "Warm", "Cold"];
const LOST = ["Lost", "Not Interested"];
const RANK = { Hot: 3, Warm: 2, Cold: 1 };
const rateOf = (l) => l.rating || l.ai_score?.label || "";
const isRecontactDue = (l) => LOST.includes(l.status) && l.recontact_at && l.recontact_at.slice(0, 10) <= todayStr();
const isFollowupDue = (l) => l.next_followup && l.next_followup.slice(0, 10) <= todayStr() && !["Won", "Lost", "Not Interested"].includes(l.status);
const LeadRating = ({ lead, testid }) => lead.rating ? (
  <span data-testid={testid} title="Set by staff" className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${SCORE_STYLE[lead.rating]}`}>{lead.rating}</span>
) : <ScoreBadge score={lead.ai_score} testid={testid} />;
const RatingSelect = ({ value, onChange, testid }) => (
  <div><label className="text-xs font-medium text-slate-500">Rating</label>
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm">
      <option value="">Not rated</option>{RATINGS.map((r) => <option key={r}>{r}</option>)}
    </select></div>
);
// service options: Master Fee List first, then legacy services not in it
const serviceOptions = (settings) => {
  const master = (settings.fee_schedule || []).filter((m) => m.professional_fee !== null && m.professional_fee !== undefined).map((m) => m.service);
  return [...master, ...(settings.services || []).filter((x) => !master.includes(x))];
};

export default function Leads() {
  const { user } = useAuth();
  const canConvert = user?.role === "admin" || (user?.permissions || []).includes("edit_clients");
  const [leads, setLeads] = useState(null);
  const [staff, setStaff] = useState([]);
  const [settings, setSettings] = useState({ lead_statuses: [], lead_sources: [], services: [] });
  const [q, setQ] = useState("");
  const [clientHits, setClientHits] = useState([]);
  const navTop = useNavigate();
  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setClientHits([]); return; }
    const h = setTimeout(() => api.get("/leads-converted-lookup", { params: { q: t } }).then((r) => setClientHits(r.data || [])).catch(() => setClientHits([])), 300);
    return () => clearTimeout(h);
  }, [q]);
  const [fStatus, setFStatus] = useState("");
  const [fScore, setFScore] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [fFollow, setFFollow] = useState(false);
  const [tab, setTab] = useState("open");
  const [fReason, setFReason] = useState("");
  const [staffF, setStaffF] = useStaffParam();
  const [winback, setWinback] = useState(null);
  const scoped = useMemo(() => (leads || []).filter((l) => !staffF || l.assigned_staff === staffF), [leads, staffF]);
  const openLeads = useMemo(() => scoped.filter((l) => !LOST.includes(l.status)), [scoped]);
  const lostLeads = useMemo(() => scoped.filter((l) => LOST.includes(l.status)), [scoped]);
  const reopen = async (l, e) => {
    e.preventDefault(); e.stopPropagation();
    try { await api.put(`/leads/${l.id}`, { status: "Contacted" }); toast.success(`${l.name} reopened`); load(); } catch (err) { toast.error(apiErr(err)); }
  };
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  const [sp, setSp] = useSearchParams();
  const wantLead = sp.get("lead");
  useEffect(() => {
    if (!wantLead || !leads) return;
    const l = leads.find((x) => x.id === wantLead);
    if (!l) toast.message("This item was removed");
    else if (sp.get("winback") === "1" && LOST.includes(l.status)) { setTab("lost"); setWinback(l); }
    else setSelected(l);
    const p = new URLSearchParams(sp); p.delete("lead"); p.delete("winback"); setSp(p, { replace: true });
  }, [wantLead, leads]); // eslint-disable-line
  useEffect(() => {
    load();
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const out = (tab === "lost" ? lostLeads : openLeads).filter((l) =>
      (!fStatus || l.status === fStatus) &&
      (!fScore || (fScore === "unscored" ? !rateOf(l) : rateOf(l) === fScore)) &&
      (!fFollow || (tab === "lost" ? isRecontactDue(l) : isFollowupDue(l))) &&
      (!fReason || (l.lost_reason || "") === fReason) &&
      (!q || [l.name, l.email, l.phone, l.service_interested].join(" ").toLowerCase().includes(q.toLowerCase())));
    if (sortBy === "score") out.sort((a, b) => ((RANK[rateOf(b)] || 0) - (RANK[rateOf(a)] || 0)) || ((b.ai_score?.value ?? -1) - (a.ai_score?.value ?? -1)));
    return out;
  }, [openLeads, lostLeads, tab, q, fStatus, fScore, sortBy, fFollow, fReason]);
  const staffName = (id) => staff.find((s) => s.id === id)?.name || "";

  const stats = useMemo(() => {
    const all = openLeads;
    return {
      total: all.length,
      newCount: all.filter((l) => l.status === "New").length,
      followups: all.filter(isFollowupDue).length,
      hot: all.filter((l) => rateOf(l) === "Hot").length,
    };
  }, [openLeads]);

  return (
    <div>
      <PageHeader title="Leads" subtitle="Track prospects from first contact to converted client.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-lead-btn" className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm"><Plus className="h-4 w-4" /> New Lead</button>
          </DialogTrigger>
          <LeadForm staff={staff} settings={settings} onDone={() => { setOpen(false); load(); setTimeout(load, 3500); }} />
        </Dialog>
      </PageHeader>

      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 mb-4" data-testid="lead-tabs">
        {[["open", "Open", openLeads.length], ["lost", "Lost", lostLeads.length]].map(([k, l, n]) => (
          <button key={k} data-testid={`lead-tab-${k}`} onClick={() => { setTab(k); setFStatus(""); setFFollow(false); setFReason(""); }} className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 ${tab === k ? "bg-primary text-white" : "text-slate-500"}`}>
            {l}<span className={`text-[11px] px-1.5 rounded-full ${tab === k ? "bg-white/20" : "bg-slate-100"}`}>{n}</span>
          </button>
        ))}
      </div>
      {tab === "open" && <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[["total", "Total Leads", stats.total, "text-slate-900", !fFollow && !fStatus, () => { setFFollow(false); setFStatus(""); }],
          ["new", "New", stats.newCount, "text-blue-600", !fFollow && fStatus === "New", () => { setFFollow(false); setFStatus("New"); }],
          ["followups", "Follow-ups Due", stats.followups, "text-amber-600", fFollow, () => { setFStatus(""); setFFollow(!fFollow); }],
          ["hot", "Hot Leads", stats.hot, "text-rose-600", !fFollow && fScore === "Hot", () => { setFFollow(false); setFStatus(""); setFScore(fScore === "Hot" ? "" : "Hot"); }]].map(([k, l, v, c, active, onClick]) => (
          <button key={k} type="button" data-testid={`lead-kpi-${k}`} onClick={onClick} className={`text-left rounded-2xl transition-shadow hover:shadow-md ${active ? "ring-2 ring-primary/50" : ""}`}>
            <Card className="p-4 h-full">
              <div className="text-xs font-medium text-slate-500">{l}</div>
              <div className={`text-2xl font-head font-extrabold mt-1 ${c}`}>{v}</div>
            </Card>
          </button>
        ))}
      </div>}

      <div className="flex flex-wrap gap-2 mb-4">
        <StaffFilterSelect value={staffF} onChange={setStaffF} myLabel="My leads" testid="lead-filter-staff" />
        <input data-testid="lead-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads..." className="px-3 py-2 rounded-xl border border-slate-200 text-sm flex-1 min-w-[200px]" />
        {tab === "lost" && <>
          <select data-testid="lead-filter-reason" value={fReason} onChange={(e) => setFReason(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm">
            <option value="">All reasons</option>{[...new Set([...(settings.lost_reasons || []), ...lostLeads.map((l) => l.lost_reason).filter(Boolean)])].map((r) => <option key={r}>{r}</option>)}
          </select>
          <button type="button" data-testid="lead-filter-recontact" onClick={() => setFFollow(!fFollow)} className={`px-3 py-2 rounded-xl border text-sm font-semibold inline-flex items-center gap-1.5 ${fFollow ? "bg-amber-500 text-white border-amber-500" : "border-slate-200 text-amber-700 bg-white"}`}>
            <RotateCcw className="h-3.5 w-3.5" /> Re-contact due ({lostLeads.filter(isRecontactDue).length})
          </button>
        </>}
        {fFollow && tab === "open" && <button data-testid="lead-followup-filter-clear" onClick={() => setFFollow(false)} className="px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold">Follow-ups due ✕</button>}
        <select data-testid="lead-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm">
          <option value="">All statuses</option>
          {settings.lead_statuses?.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="lead-filter-score" value={fScore} onChange={(e) => setFScore(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm">
          <option value="">All ratings</option>{RATINGS.map((x) => <option key={x}>{x}</option>)}<option value="unscored">Not rated</option>
        </select>
        {user?.ai_enabled && (leads || []).some((l) => !l.ai_score) && <ScoreAllButton onDone={load} />}
        <select data-testid="lead-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm">
          <option value="newest">Newest first</option><option value="score">Hottest first</option>
        </select>
      </div>
      {clientHits.length > 0 && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm" data-testid="lead-client-hits">
          <div className="font-semibold text-emerald-800 flex items-center gap-1.5"><UserCheck className="h-4 w-4" /> Already a client — converted leads live in Clients</div>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {clientHits.map((c) => (
              <button key={c.id} data-testid={`lead-client-hit-${c.id}`} onClick={() => navTop(`/admin/clients/${c.id}`)} className="px-3 py-1 rounded-lg bg-white border border-emerald-200 text-emerald-800 text-xs font-semibold hover:bg-emerald-100">
                {c.full_name}{c.email ? ` · ${c.email}` : c.phone ? ` · ${c.phone}` : ""} → Open profile
              </button>
            ))}
          </div>
        </div>
      )}

      {!leads ? <Loader /> : filtered.length === 0 ? (
        <Card className="p-6"><EmptyState icon={UserPlus} title="No leads yet" description="Add your first prospect to start tracking." /></Card>
      ) : (
        <LeadList leads={filtered} lost={tab === "lost"} staffName={staffName} onOpen={setSelected} onReopen={reopen} onLogged={load} />
      )}

      {winback && <WinBackDialog lead={winback} onClose={() => setWinback(null)} onLogged={load} />}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        {selected && <LeadDetail lead={selected} staff={staff} settings={settings} canConvert={canConvert}
          onUpdate={() => { load(); setSelected(null); }} />}
      </Dialog>
    </div>
  );
}

function LeadForm({ staff, settings, onDone }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", service_interested: "", lead_source: "Website", status: "New", assigned_staff: "", next_followup: "", notes: "", rating: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try { await api.post("/leads", f); toast.success("Lead added"); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const field = (k, label, type = "text") => (
    <div><label className="text-xs font-medium text-slate-500">{label}</label>
      <input data-testid={`lead-${k}`} type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></div>
  );
  return (
    <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
      <div className="space-y-3">
        {field("name", "Full name *")}
        <div className="grid grid-cols-2 gap-3">{field("email", "Email", "email")}{field("phone", "Phone")}</div>
        <DuplicateGuard email={f.email} phone={f.phone} testId="new-lead-duplicate-warning" />
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-medium text-slate-500">Service interested</label>
            <select data-testid="lead-service" value={f.service_interested} onChange={(e) => setF({ ...f, service_interested: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"><option value="">—</option>{serviceOptions(settings).map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><label className="text-xs font-medium text-slate-500">Source</label>
            <select value={f.lead_source} onChange={(e) => setF({ ...f, lead_source: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm">{settings.lead_sources?.map((s) => <option key={s}>{s}</option>)}</select></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-medium text-slate-500">Status</label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm">{settings.lead_statuses?.map((s) => <option key={s}>{s}</option>)}</select></div>
          {field("next_followup", "Next follow-up", "date")}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-medium text-slate-500">Assigned to</label>
            <select value={f.assigned_staff} onChange={(e) => setF({ ...f, assigned_staff: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"><option value="">Unassigned</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          <RatingSelect value={f.rating} onChange={(v) => setF({ ...f, rating: v })} testid="lead-rating" />
        </div>
        <div><label className="text-xs font-medium text-slate-500">Notes</label>
          <textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></div>
        <DialogFooter><button data-testid="lead-submit" onClick={submit} disabled={saving} className="bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm w-full disabled:opacity-60">{saving ? "Saving..." : "Add lead"}</button></DialogFooter>
      </div>
    </DialogContent>
  );
}

function LeadDetail({ lead, staff, settings, canConvert, onUpdate }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ ...lead, next_followup: lead.next_followup ? lead.next_followup.slice(0, 10) : "", lost_reason: lead.lost_reason || "", recontact_at: (lead.recontact_at || "").slice(0, 10) });
  const isLost = LOST.includes(f.status);
  const wasLost = LOST.includes(lead.status);
  const defaultRecontact = () => { const dt = new Date(); dt.setDate(dt.getDate() + (settings.lost_recontact_days || 90)); return dt.toISOString().slice(0, 10); };
  const setStatus = (v) => setF({ ...f, status: v, ...(LOST.includes(v) && !wasLost ? { lost_reason: "", recontact_at: defaultRecontact() } : {}) });
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [ai, setAi] = useState(lead);
  const [dups, setDups] = useState([]);
  const [mergeWith, setMergeWith] = useState(null);
  const canEdit = user?.role === "admin" || (user?.permissions || []).includes("edit_leads");
  useEffect(() => { if (canEdit) api.get(`/leads/${lead.id}/duplicates`).then((r) => setDups(r.data)).catch(() => {}); }, [lead.id, canEdit]);

  const save = async () => {
    if (isLost && !f.lost_reason) { toast.error("Pick a reason for losing this lead"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/leads/${lead.id}`, { status: f.status, next_followup: f.next_followup || null, assigned_staff: f.assigned_staff || null, service_interested: f.service_interested, phone: f.phone, email: f.email, rating: f.rating || "",
        ...(isLost ? { lost_reason: f.lost_reason, recontact_at: f.recontact_at || null } : {}) });
      if (data?.converted) {
        toast.success(data.linked_existing ? `Won — linked to existing client ${data.client_name}` : `Won — ${data.client_name} is now a client`, { description: "Converted leads live in Clients only." });
        nav(`/admin/clients/${data.converted_client_id}`);
        return;
      }
      toast.success("Lead updated"); onUpdate();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const convert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert`, {});
      toast.success(`Converted to client${data.temp_password ? ` · temp password: ${data.temp_password}` : ""}`);
      nav(`/admin/clients/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setConverting(false); }
  };
  return (
    <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="flex items-center gap-2">{lead.name} <StatusBadge status={f.status} /></DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-medium text-slate-500">Email</label><input value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></div>
          <div><label className="text-xs font-medium text-slate-500">Phone</label><input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></div>
        </div>
        {dups.length > 0 && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900 space-y-1.5" data-testid="lead-possible-duplicates">
            {dups.map((x) => (
              <div key={x.id} className="flex items-center justify-between gap-2">
                <span>Possible duplicate: <b>{x.name}</b>{x.email ? ` · ${x.email}` : ""}{x.phone ? ` · ${x.phone}` : ""}</span>
                <button type="button" data-testid={`lead-merge-btn-${x.id}`} onClick={() => setMergeWith(x)} disabled={lead.converted_client_id && x.converted_client_id}
                  title={lead.converted_client_id && x.converted_client_id ? "Both leads are already converted" : ""}
                  className="px-3 py-1 rounded-lg bg-amber-600 text-white font-semibold shrink-0 disabled:opacity-40">Merge</button>
              </div>
            ))}
          </div>
        )}
        {mergeWith && <LeadMergeDialog lead={lead} other={mergeWith} staff={staff} onClose={() => setMergeWith(null)} onMerged={() => { setMergeWith(null); onUpdate(); }} />}
        {!lead.converted_client_id && <DuplicateGuard email={f.email} phone={f.phone} testId="lead-duplicate-warning" />}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-medium text-slate-500">Status</label>
            <select data-testid="lead-detail-status" value={f.status} onChange={(e) => setStatus(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm">{settings.lead_statuses?.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><label className="text-xs font-medium text-slate-500">Next follow-up</label><input data-testid="lead-detail-followup" type="date" value={f.next_followup} onChange={(e) => setF({ ...f, next_followup: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></div>
        </div>
        {isLost && (
          <div className="grid grid-cols-2 gap-3 rounded-xl border border-rose-200 bg-rose-50/60 p-3" data-testid="lead-lost-panel">
            <div><label className="text-xs font-medium text-rose-700">Why was it lost? *</label>
              <select data-testid="lead-lost-reason" value={f.lost_reason} onChange={(e) => setF({ ...f, lost_reason: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-rose-200 bg-white text-sm">
                <option value="">Pick a reason…</option>{(settings.lost_reasons || []).map((r) => <option key={r}>{r}</option>)}
                {f.lost_reason && !(settings.lost_reasons || []).includes(f.lost_reason) && <option>{f.lost_reason}</option>}
              </select></div>
            <div><label className="text-xs font-medium text-rose-700">Remind me to re-contact on</label>
              <input data-testid="lead-recontact-date" type="date" value={f.recontact_at} onChange={(e) => setF({ ...f, recontact_at: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-rose-200 bg-white text-sm" /></div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-medium text-slate-500">Service</label>
            <select data-testid="lead-detail-service" value={f.service_interested || ""} onChange={(e) => setF({ ...f, service_interested: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"><option value="">—</option>{serviceOptions(settings).map((s) => <option key={s}>{s}</option>)}{f.service_interested && !serviceOptions(settings).includes(f.service_interested) && <option>{f.service_interested}</option>}</select></div>
          <div><label className="text-xs font-medium text-slate-500">Assigned to</label>
            <select value={f.assigned_staff || ""} onChange={(e) => setF({ ...f, assigned_staff: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"><option value="">Unassigned</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
        </div>
        <div className="grid grid-cols-2 gap-3"><RatingSelect value={f.rating} onChange={(v) => setF({ ...f, rating: v })} testid="lead-detail-rating" /></div>
        {(user?.ai_enabled || ai.ai_score || ai.ai_service_suggestion) && <LeadAiPanel aiOn={!!user?.ai_enabled} lead={ai} onRescore={setAi} onUseService={(svc) => setF({ ...f, service_interested: svc })} current={f.service_interested} />}
        <div className="rounded-xl border border-slate-200 bg-white p-3"><AppointmentsPanel leadId={lead.id} /></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="lead-notes-log"><LeadNoteLog leadId={lead.id} /></div>
        <div className="flex gap-2 pt-1">
          <button data-testid="lead-save" onClick={save} disabled={saving} className="flex-1 bg-secondary text-white px-4 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
          {canConvert && (
            lead.converted_client_id ? (
              <button onClick={() => nav(`/admin/clients/${lead.converted_client_id}`)} className="flex-1 bg-emerald-600 text-white px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2"><UserCheck className="h-4 w-4" /> View client</button>
            ) : (
              <button data-testid="lead-convert" onClick={convert} disabled={converting} className="flex-1 bg-primary text-white px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"><UserCheck className="h-4 w-4" /> {converting ? "Converting..." : "Convert to client"}</button>
            )
          )}
        </div>
      </div>
    </DialogContent>
  );
}

function LeadAiPanel({ aiOn, lead, onRescore, onUseService, current }) {
  const [busy, setBusy] = useState(false);
  const rescore = async () => {
    setBusy(true);
    try { const r = await api.post(`/leads/${lead.id}/ai-score`); onRescore(r.data); toast.success("Lead re-scored"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const sug = lead.ai_service_suggestion;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="lead-ai-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600"><Sparkles className="h-3.5 w-3.5 text-primary" /> AI lead score
          <ScoreBadge score={lead.ai_score} testid="lead-detail-score" />
          {!lead.ai_score && <span className="text-slate-400 font-normal">{lead.ai_score_error || "Not scored yet"}</span>}
        </div>
        {aiOn && <button data-testid="lead-rescore" onClick={rescore} disabled={busy} className="text-[11px] font-semibold text-primary inline-flex items-center gap-1 disabled:opacity-50"><RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Scoring…" : "Rescore"}</button>}
      </div>
      {lead.ai_score?.reason && <div className="text-xs text-slate-500" data-testid="lead-detail-reason">{lead.ai_score.reason}</div>}
      {sug && (
        <div className="text-xs flex flex-wrap items-center gap-1.5" data-testid="lead-service-suggestion">
          <span className="text-slate-500">Suggested service:</span>
          {sug.service ? (
            current === sug.service ? <span className="font-semibold text-emerald-700">{sug.service} ✓</span> : (
              <button data-testid="lead-use-suggested-service" onClick={() => onUseService(sug.service)} className="px-2 py-0.5 rounded-full border border-primary/30 text-primary hover:bg-primary/5 font-semibold">Use “{sug.service}”</button>
            )
          ) : <span className="text-amber-700">No clear match in the Master Fee List</span>}
        </div>
      )}
      {aiOn && <div className="text-[10px] text-slate-400">Scores update automatically when notes or service change. Save the lead to keep a suggested service.</div>}
    </div>
  );
}

function ScoreAllButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/leads/ai-score-unscored");
      toast[r.data.failed ? "warning" : "success"](`Scored ${r.data.scored} of ${r.data.total} leads${r.data.failed ? ` — ${r.data.failed} failed, try again shortly` : ""}`);
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <button data-testid="lead-score-all" onClick={run} disabled={busy} className="px-3 py-2 rounded-xl border border-primary/30 text-primary text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
      <Sparkles className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} /> {busy ? "Scoring…" : "Score unscored leads"}
    </button>
  );
}

const fmtDay = (v) => (v ? new Date(`${v.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

function LeadWhen({ l, lost }) {
  if (lost) {
    const due = isRecontactDue(l);
    return (
      <div className="min-w-0">
        <div className="truncate text-slate-700" title={l.lost_reason || ""} data-testid={`lead-lost-reason-${l.id}`}>{l.lost_reason || <span className="text-slate-300">No reason</span>}</div>
        {l.recontact_at && <div className={`truncate text-xs inline-flex items-center gap-1 ${due ? "text-amber-600 font-semibold" : "text-slate-400"}`} data-testid={`lead-recontact-${l.id}`}><RotateCcw className="h-3 w-3" />{due ? "Re-contact due" : "Re-contact"} {fmtDay(l.recontact_at)}</div>}
      </div>
    );
  }
  if (!l.next_followup) return <span className="text-slate-300">—</span>;
  const overdue = isFollowupDue(l);
  return <span className={`inline-flex items-center gap-1 ${overdue ? "text-amber-600 font-semibold" : "text-slate-500"}`}>{overdue ? <AlertCircle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}{fmtDay(l.next_followup)}</span>;
}

function LeadActions({ l, lost, onReopen, onLogged }) {
  return (
    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
      {lost && <WinBackButton lead={l} onLogged={onLogged} testid={`lead-winback-${l.id}`} />}
      {lost && <button type="button" data-testid={`lead-reopen-${l.id}`} onClick={(e) => onReopen(l, e)} className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs font-semibold text-primary hover:bg-primary/5">Reopen</button>}
      <WhatsAppButton target={{ kind: "lead", id: l.id, name: l.name, phone: l.phone, service: l.service_interested }} testid={`lead-whatsapp-${l.id}`} onLogged={onLogged} />
    </div>
  );
}

function LeadList({ leads, lost, staffName, onOpen, onReopen, onLogged }) {
  return (
    <>
      <Card className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[900px] table-fixed text-sm" data-testid="lead-table">
          <colgroup><col className="w-[20%]" /><col className="w-[18%]" /><col className="w-[15%]" /><col className="w-[12%]" /><col className="w-[19%]" /><col className="w-[110px]" /><col className={lost ? "w-[210px]" : "w-[110px]"} /></colgroup>
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>{["Lead", "Contact", "Service · Source", "Assigned to", lost ? "Lost reason" : "Follow-up", "Status"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold leading-tight align-bottom break-words">{h}</th>)}<th className="text-right px-4 py-3 font-semibold align-bottom">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {leads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => onOpen(l)} className="align-top hover:bg-slate-50 transition-colors cursor-pointer">
                <td className="px-4 py-3">
                  <div className="truncate font-semibold text-slate-900" title={l.name}>{l.name}</div>
                  <div className="flex items-center gap-1.5 mt-0.5"><LeadRating lead={l} testid={`lead-score-${l.id}`} />{l.converted_client_id && <span className="text-[10px] font-bold text-emerald-600 inline-flex items-center gap-1"><UserCheck className="h-3 w-3" />Converted</span>}</div>
                </td>
                <td className="px-4 py-3 text-slate-500"><div className="truncate" title={l.email || ""}>{l.email || "—"}</div><div className="truncate text-xs" title={l.phone || ""}>{l.phone || "—"}</div></td>
                <td className="px-4 py-3 text-slate-500"><div className="truncate" title={l.service_interested || ""}>{l.service_interested || "—"}</div><div className="truncate text-xs text-slate-400">{l.lead_source || "—"}</div></td>
                <td className="px-4 py-3 text-slate-500"><div className="truncate" title={staffName(l.assigned_staff)}>{staffName(l.assigned_staff) || <span className="text-slate-300">Unassigned</span>}</div></td>
                <td className="px-4 py-3"><LeadWhen l={l} lost={lost} /></td>
                <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                <td className="px-4 py-2.5"><LeadActions l={l} lost={lost} onReopen={onReopen} onLogged={onLogged} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <div className="md:hidden grid gap-3">
        {leads.map((l) => (
          <Card key={l.id} data-testid={`lead-card-${l.id}`} onClick={() => onOpen(l)} className="p-3.5 flex flex-wrap items-start gap-3 overflow-hidden cursor-pointer">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 font-head font-bold text-primary">{(l.name || "?").charAt(0)}</div>
            <div className="flex-1 min-w-0 space-y-0.5">
              <div className="font-semibold text-slate-900 truncate">{l.name}</div>
              <div className="text-xs text-slate-500 truncate">{l.service_interested || "—"}{l.phone ? ` · ${l.phone}` : ""}</div>
              <div className="text-xs"><LeadWhen l={l} lost={lost} /></div>
            </div>
            <div className="w-[104px] shrink-0 flex flex-col items-end gap-2"><StatusBadge status={l.status} /></div>
            <div className="basis-full flex justify-end"><LeadActions l={l} lost={lost} onReopen={onReopen} onLogged={onLogged} /></div>
          </Card>
        ))}
      </div>
    </>
  );
}
